<template>
    <div>
        <AppInputLabel v-if="label" :name="name" :label="label" :hasError="hasError(name).value" :required="required" />
        <div>
            <input :class="{'w-full':isFullWidth, 'w-1/2': !isFullWidth, 'text-right': textRight}" type="text" :value="displayValue" @focus="handleFocus" @blur="handleBlur"
                @input="handleInput" inputmode="decimal" />
        </div>
        <AppInputError v-show="hasError(name)" :error="errors.get(name)" />

    </div>
</template>
<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, inject, onBeforeUnmount } from 'vue';
import AppInputLabel from './AppInputLabel.vue';
import AppInputError from './AppInputError.vue';
import { ValidationContextKey } from '@/models/Context';
import useFormValidation from '@/modules/useFormValidation';

const props = defineProps<{
    required?: boolean;
    name: string;
    label?: string;
    modelValue: number | null;
    currency: string;
    isFullWidth?: boolean;
    textRight?: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void;
}>();


// Create a default validation context if none is provided
const defaultContext = {
  errors: new Map<string, string>(),
  validators: {} as Record<string, (key: string, validator: string) => void>,
  registerValidator: (key: string, validator: (key: string, validator: string) => void) => {
    defaultContext.validators[key] = validator;
  }
};

const context = inject(ValidationContextKey, defaultContext);
const { validateRequiredNumberField, clearValidator, clearError, errors, registerValidator, hasError } = useFormValidation(context);

const validateInput = () => {
    clearError(props.name);

    if (props.required) {
        validateRequiredNumberField(props.name, props.label ?? "", props.modelValue ?? 0);
    }
};

registerValidator(props.name, validateInput);

const isFocused = ref(false);
const inputValue = ref(props.modelValue ?? 0 );

const displayValue = computed(() => {
    if (isFocused.value) {
        return inputValue.value;
    } else if (inputValue.value === 0) {
        return "";
    } else {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: props.currency,
        }).format(Number(inputValue.value));
    }
});

const handleFocus = (event: FocusEvent) => {
    isFocused.value = true;

    inputValue.value = inputValue.value || props.modelValue || 0;

    setTimeout(() => {
        const input = event.target as HTMLInputElement;
        input.select();
    }, 0);
};

const handleBlur = () => {
    isFocused.value = false;
    validateInput();
    emit('update:modelValue', Number(inputValue.value));
};

const handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    inputValue.value = input.value.replace(/[^0-9.]/g, '');
};

onBeforeUnmount(() => {
    clearValidator(props.name);
    clearError(props.name);
});
</script>
